class BeforeAfterCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.comparisonRanges = Array.from(this.querySelectorAll('[data-comparison-range]'));
    this.layout = this.dataset.layout || 'carousel';
    this.gridVisibleMobile = Number(this.dataset.gridVisibleMobile || 1);
    this.gridVisibleDesktop = Number(this.dataset.gridVisibleDesktop || 6);
    this.gridExpanded = false;
    this.gridSeeMoreButton = this.querySelector('[data-grid-see-more]');
    this.gridSeeMoreWrap = this.querySelector('[data-grid-see-more-wrap]');
    this.autoplayInterval = Number(this.dataset.autoplayInterval || 5000);
    this.autoplayTimer = null;

    if (!this.track || this.cards.length === 0) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.scrollToCard(Number(dot.dataset.carouselIndex || 0));
        this.startAutoplay();
      });
    });

    this.comparisonRanges.forEach((range) => {
      this.syncComparisonRange(range);
      range.addEventListener('input', () => this.syncComparisonRange(range));
    });

    this.gridSeeMoreButton?.addEventListener('click', () => {
      this.gridExpanded = true;
      this.updateGridVisibility();
    });

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    if (this.isGrid) {
      this.updateGridVisibility();
    } else {
      this.updateDots();
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.stopAutoplay();
  }

  handleScroll() {
    window.requestAnimationFrame(() => this.updateActiveDot());
  }

  handleResize() {
    this.stopAutoplay();

    if (this.isGrid) {
      this.updateGridVisibility();
      return;
    }

    this.updateDots();
    this.startAutoplay();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay();
      return;
    }

    this.startAutoplay();
  }

  scrollToCard(index) {
    const card = this.cards[index];
    if (!card) return;

    this.track.scrollTo({
      left: card.offsetLeft - this.track.offsetLeft,
      behavior: 'smooth',
    });
  }

  syncComparisonRange(range) {
    const media = range.closest('[data-comparison-media]');
    if (!media) return;

    media.style.setProperty('--arca-before-after-compare', `${range.value}%`);
  }

  updateDots() {
    if (this.isGrid) return;

    const visibleCount = this.visibleCardCount;
    const maxStartIndex = Math.max(0, this.cards.length - visibleCount);

    this.dots.forEach((dot, index) => {
      dot.hidden = index > maxStartIndex;
    });

    this.updateActiveDot();
  }

  updateActiveDot() {
    const currentIndex = this.currentCardIndex;
    const visibleCount = this.visibleCardCount;
    const maxStartIndex = Math.max(0, this.cards.length - visibleCount);
    const activeIndex = Math.min(currentIndex, maxStartIndex);

    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;

      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', String(isActive));
    });
  }

  startAutoplay() {
    if (
      this.isGrid ||
      this.autoplayTimer ||
      this.autoplayInterval <= 0 ||
      this.cards.length <= this.visibleCardCount ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.hidden
    ) {
      return;
    }

    this.autoplayTimer = window.setInterval(() => this.advance(), this.autoplayInterval);
  }

  stopAutoplay() {
    if (!this.autoplayTimer) return;

    window.clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  advance() {
    const visibleCount = this.visibleCardCount;
    const maxStartIndex = Math.max(0, this.cards.length - visibleCount);

    if (maxStartIndex === 0) return;

    const nextIndex = this.currentCardIndex >= maxStartIndex ? 0 : this.currentCardIndex + 1;
    this.scrollToCard(nextIndex);
  }

  get currentCardIndex() {
    const trackLeft = this.track.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    this.cards.forEach((card, index) => {
      const cardLeft = card.offsetLeft - this.track.offsetLeft;
      const distance = Math.abs(cardLeft - trackLeft);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  get visibleCardCount() {
    const firstCard = this.cards[0];
    if (!firstCard) return 1;

    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0') || 0;
    const cardWidth = firstCard.getBoundingClientRect().width;

    if (cardWidth <= 0) return 1;

    return Math.max(1, Math.round((this.track.clientWidth + gap) / (cardWidth + gap)));
  }

  updateGridVisibility() {
    const visibleLimit = this.gridVisibleLimit;
    const shouldShowMore = !this.gridExpanded && this.cards.length > visibleLimit;

    this.cards.forEach((card, index) => {
      card.hidden = shouldShowMore && index >= visibleLimit;
    });

    if (this.gridSeeMoreWrap) {
      this.gridSeeMoreWrap.hidden = !shouldShowMore;
    }
  }

  get gridVisibleLimit() {
    if (window.matchMedia('(min-width: 750px)').matches) {
      return Math.max(1, this.gridVisibleDesktop);
    }

    return Math.max(1, this.gridVisibleMobile);
  }

  get isGrid() {
    return this.layout === 'grid';
  }
}

if (!customElements.get('before-after-carousel')) {
  customElements.define('before-after-carousel', BeforeAfterCarousel);
}
